import { type RewardLadderTier, type RewardTier, RewardTierState } from '@/types/RewardTier'

/**
 * Minimum rail fill so a sliver of progress always shows, even at a $0 cart (per Figma / creative).
 * Mirrors the legacy `RewardsProgress` bar's old min-width behavior.
 */
const MIN_RAIL_FILL_FRACTION = 0.03

/**
 * Stamps each ladder tier with its state relative to a cart subtotal, plus the amount still to spend
 * to reach it. Exactly one tier is `Current` — the next one to unlock — and everything above it is
 * `Locked`.
 *
 * Pure (no store access) for the same reason `buildRewardsHeading` is: the debug harness drives the
 * real `RewardsTracker` from a synthetic ladder, and the locked/current/unlocked rule is the one piece
 * of that it must not reimplement. `catalogStore.rewardTiers` applies it to the live ladder.
 */
export function stampRewardTierStates(
  ladder: RewardLadderTier[],
  effectiveSubtotal: number
): RewardTier[] {
  let currentAssigned = false

  return ladder.map((tier): RewardTier => {
    let tierState: RewardTierState
    if (effectiveSubtotal >= tier.minimumSubtotal) {
      tierState = RewardTierState.Unlocked
    } else if (!currentAssigned) {
      tierState = RewardTierState.Current
      currentAssigned = true
    } else {
      tierState = RewardTierState.Locked
    }

    return {
      ...tier,
      state: tierState,
      amountToUnlock: Math.max(0, tier.minimumSubtotal - effectiveSubtotal)
    }
  })
}

/**
 * Width of the magenta fill along the tracker's rail, as a percentage (0–100).
 *
 * Nodes are evenly distributed with a half-gap at each end (node `i` centers at `(i + 0.5) / n`), so
 * the fill can show a leading nub before the first node and a partial fill into the current tier. It
 * is derived from the stamped tiers themselves — `minimumSubtotal − amountToUnlock` is the effective
 * subtotal — which is what lets one signature serve a live ladder and a synthetic one.
 *
 * **Extracted at A2b, and the extraction was overdue.** `RewardsTracker` owned this and the debug
 * harness copied it to build the sequence's `fromPct` / `toPct`; the copy immediately dropped
 * {@link MIN_RAIL_FILL_FRACTION}, so `fromPct` reported 0% while the bar on screen sat at 3% and every
 * cold-start sequence snapped backwards before animating forwards. Same treatment
 * {@link stampRewardTierStates} got, for the same reason: three callers, one rule.
 *
 * To evaluate it at a subtotal other than the cart's, stamp the ladder first —
 * `rewardRailFillPercent(stampRewardTierStates(ladder, subtotal))`.
 */
export function rewardRailFillPercent(tiers: RewardTier[]): number {
  const n = tiers.length
  if (n === 0) return 0

  const positionOf = (index: number): number => (index + 0.5) / n

  // All unlocked → fill the entire rail, including the trailing segment past the last node.
  if (tiers.every((tier) => tier.state === RewardTierState.Unlocked)) return 100

  const currentIndex = tiers.findIndex((tier) => tier.state === RewardTierState.Current)
  if (currentIndex === -1) return 0

  const current = tiers[currentIndex]
  const effectiveSubtotal = current.minimumSubtotal - current.amountToUnlock
  const previousThreshold = currentIndex > 0 ? tiers[currentIndex - 1].minimumSubtotal : 0
  const previousPosition = currentIndex > 0 ? positionOf(currentIndex - 1) : 0
  const span = current.minimumSubtotal - previousThreshold
  const progress =
    span > 0 ? Math.min(1, Math.max(0, (effectiveSubtotal - previousThreshold) / span)) : 0

  const fraction = previousPosition + progress * (positionOf(currentIndex) - previousPosition)
  return Math.max(MIN_RAIL_FILL_FRACTION, fraction) * 100
}
